import React, { useEffect, useState } from "react";
import { report } from "../../api/merchant";

const EventIncomeDetailsModal = ({ showIncomeDetails, onClose }) => {
  const { status, code } = showIncomeDetails;
  const [isLoading, setIsLoading] = useState(true);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);

  const statusArr = ["Upcoming", "Paid"];

  useEffect(() => {
    if (status && code) {
      handleInit();
    }
  }, [status, code]);

  const handleInit = async () => {
    setIsLoading(true);
    try {
      const response = await report({
        flag: "view_transaction",
        booking_code: code,
      });
      if (response.error === 0) {
        setTransactionDetails(response.data.transaction_details || {});
        setBankAccount(response.data.bank_account || {});
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setIsLoading(false);
  };

  const maskAccountNumber = (acc) => {
    return acc ? `${"*".repeat(acc.length - 4)}${acc.slice(-4)}` : "";
  };

  if (!status) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-4 relative overflow-y-auto max-h-[90vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        {isLoading ? (
          <div className="space-y-4">
            <div className="animate-pulse space-y-2 text-center">
              <div className="h-6 bg-gray-200 w-1/3 mx-auto rounded"></div>
              <div className="h-5 bg-gray-200 w-1/2 mx-auto rounded"></div>
            </div>
            <div className="border border-gray-300 p-4 rounded-lg space-y-2">
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-4/5 rounded"></div>
            </div>
            <div className="border border-gray-300 p-4 rounded-lg space-y-2">
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
              <div className="h-4 bg-gray-200 w-full rounded"></div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Payout Summary */}
            <div className="text-center">
              <p className="text-primary-500 text-2xl font-semibold">
                PHP {transactionDetails?.total_payout}
              </p>
              <p className="text-sm text-gray-600">
                Upcoming on {transactionDetails?.payout_date}
              </p>
            </div>

            {/* Bank Details */}
            <div className="border border-gray-300 p-4 rounded-lg space-y-1">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-800">
                  Bank Account
                </span>
                <div className="text-right">
                  <p className="text-gray-700">{bankAccount?.account_name}</p>
                  <p className="text-gray-700 text-sm">
                    {maskAccountNumber(bankAccount?.account_no)}{" "}
                    {bankAccount?.bank_name}
                  </p>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {transactionDetails?.profile_img ? (
                      <img
                        src={transactionDetails.profile_img}
                        alt="client"
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-white">
                        {transactionDetails?.client_name?.[0]}
                      </div>
                    )}
                    <span className="font-semibold text-gray-800">
                      {transactionDetails?.client_name}
                    </span>
                  </div>
                  <span
                    className={`text-white text-xs px-3 py-1 rounded-full ${
                      transactionDetails?.payout_status === "0"
                        ? "bg-yellow-500"
                        : "bg-green-500"
                    }`}
                  >
                    {statusArr[transactionDetails?.payout_status]}
                  </span>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Event</p>
                  <p className="text-sm text-gray-800">
                    {transactionDetails?.title}
                  </p>
                </div>

                <div className="flex space-x-4">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Booking Code</p>
                    <p className="text-sm text-gray-800">
                      {transactionDetails?.booking_code}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Event Date</p>
                    <p className="text-sm text-gray-800">
                      {transactionDetails?.date}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm text-gray-800">
                    {transactionDetails?.location}
                  </p>
                </div>
              </div>

              {/* Income Breakdown */}
              <div className="border-t border-gray-300 p-4 space-y-2 bg-gray-50">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>
                    PHP {transactionDetails?.contract_price} x{" "}
                    {transactionDetails?.days_count} day(s)
                  </span>
                  <span>PHP {transactionDetails?.total_amount}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Service Fee</span>
                  <span>PHP {transactionDetails?.merchant_service_fee}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-800">
                  <span>Total (PHP)</span>
                  <span>PHP {transactionDetails?.total_payout}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventIncomeDetailsModal;
